import React, { useState, useRef, useEffect } from 'react'
import PubSub from 'pubsub-js'

export default function GridEditor({
  id = "grid-editor",
  grid = { cols: 12, rowHeight: 60, gap: 2 },
  initialSlots = [],
  onChange,
  onSlotMove,
  onSlotResize,
  onSlotDelete,
  onSlotCreate,
  onSelectionChange,
  onSwapExecute,
  onRailMove,
  onClear,
  renderSlot,
  readOnly = false,
  className = "",
  safeAreaCols = 5, // Number of columns on each side for safe area
  innerCols = 12, // Number of inner content columns
}) {

  const containerRef = useRef(null)
  const totalCols = safeAreaCols * 2 + innerCols // Total grid columns including safe areas
  const initialNextId = (() => {
    const numericIds = initialSlots
      .map(s => typeof s.id === 'number' ? s.id : 0)
      .filter(id => !isNaN(id))
    return numericIds.length > 0 ? Math.max(...numericIds) + 1 : 1
  })()
  const nextId = useRef(initialNextId)
  const isOperatingRef = useRef(false)
  const isSyncingRef = useRef(false)

  // Core state
  const [slots, setSlots] = useState(initialSlots.map(slot => ({
    ...slot,
    x: slot.x + safeAreaCols // Offset all slots by safe area
  })))
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [hoveredSlot, setHoveredSlot] = useState(null)

  // Drag state
  const [dragging, setDragging] = useState(null) // slot ID being dragged
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [draggedSlotOriginal, setDraggedSlotOriginal] = useState(null)
  const [placeholderPosition, setPlaceholderPosition] = useState(null)
  const [currentMousePos, setCurrentMousePos] = useState({ x: 0, y: 0 })
  const lastDragPositionRef = useRef(null) // Track last drag position to calculate direction on move
  const justFinishedOperationRef = useRef(false) // Track if we just finished a drag/resize to prevent click deselection

  // Resize state
  const [resizing, setResizing] = useState(null) // slot ID being resized
  const [resizeDirection, setResizeDirection] = useState(null)
  const [resizeOriginal, setResizeOriginal] = useState(null)
  const [resizeOriginalSlots, setResizeOriginalSlots] = useState([])

  // Rail state
  const [draggingRail, setDraggingRail] = useState(null)
  const [rails, setRails] = useState({ vertical: [], horizontal: [], verticalRanges: new Map(), horizontalRanges: new Map() })

  // Swap state
  const [swapMode, setSwapMode] = useState(null)
  const [swapHoveredSlot, setSwapHoveredSlot] = useState(null)

  // UI state
  const [shouldDeselectOnUp, setShouldDeselectOnUp] = useState(false)

  // Sync with external slots changes
  const prevInitialSlotsRef = useRef(null)
  useEffect(() => {
    if (initialSlots && initialSlots.length >= 0 && !isOperatingRef.current) {
      // Create a key to detect if initialSlots actually changed
      const newKey = JSON.stringify(initialSlots.map(s => ({ id: s.id, x: s.x, y: s.y, w: s.w, h: s.h })))
      const prevKey = prevInitialSlotsRef.current

      if (newKey !== prevKey) {
        prevInitialSlotsRef.current = newKey
        isSyncingRef.current = true
        setSlots(initialSlots.map(slot => ({
          ...slot,
          x: slot.x + safeAreaCols // Offset all slots by safe area
        })))

        // Clear selection when external slots change
        setSelectedSlot(null)

        // Reset syncing flag after a microtask to ensure onChange doesn't fire
        Promise.resolve().then(() => {
          isSyncingRef.current = false
        })
      }
    }
  }, [initialSlots, safeAreaCols])

  // Keep selectedSlot reference in sync when slots array changes
  useEffect(() => {
    if (selectedSlot && !isOperatingRef.current) {
      const updatedSlot = slots.find(s => s.id === selectedSlot.id)

      if (!updatedSlot) {
        // Slot was removed from slots array, clear selection
        setSelectedSlot(null)
      } else if (updatedSlot.x !== selectedSlot.x || updatedSlot.y !== selectedSlot.y || updatedSlot.w !== selectedSlot.w || updatedSlot.h !== selectedSlot.h) {
        setSelectedSlot(updatedSlot)
      }
    }
  }, [slots, selectedSlot])

  // Core utility functions
  const getContainerWidth = () => {
    if (!containerRef.current) return 800
    return containerRef.current.offsetWidth
  }

  const getCellWidth = () => {
    return (getContainerWidth() - grid.gap * (totalCols + 1)) / totalCols
  }

  const pixelToGrid = (px, py) => {
    const cellWidth = getCellWidth()
    const x = Math.round((px - grid.gap) / (cellWidth + grid.gap))
    const y = Math.round((py - grid.gap) / (grid.rowHeight + grid.gap))
    return {
      x: Math.max(0, Math.min(totalCols, x)),
      y: Math.max(0, y)
    }
  }

  const gridToPixel = (gx, gy) => {
    const cellWidth = getCellWidth()
    return {
      x: gx * (cellWidth + grid.gap) + grid.gap,
      y: gy * (grid.rowHeight + grid.gap) + grid.gap
    }
  }

  const isColliding = (rect1, rect2) => {
    return !(
      rect1.x >= rect2.x + rect2.w ||
      rect1.x + rect1.w <= rect2.x ||
      rect1.y >= rect2.y + rect2.h ||
      rect1.y + rect1.h <= rect2.y
    )
  }

  const findNextAvailablePosition = (w, h, excludeIds = []) => {
    // Find positions within the inner content area only
    const minX = safeAreaCols
    const maxX = safeAreaCols + innerCols

    for (let y = 0; y < 100; y++) {
      for (let x = minX; x <= maxX - w; x++) {
        const testSlot = { x, y, w, h }
        const collides = slots.some(s =>
          !excludeIds.includes(s.id) && isColliding(testSlot, s)
        )
        if (!collides) {
          return { x, y }
        }
      }
    }
    return { x: minX, y: 0 }
  }

  const ensureSlotsWithinGrid = (slotsToCheck) => {
    const minX = safeAreaCols
    const maxX = safeAreaCols + innerCols

    return slotsToCheck.map(slot => {
      let adjusted = { ...slot }

      // Constrain to inner content area
      if (adjusted.x < minX) adjusted.x = minX

      if (adjusted.x + adjusted.w > maxX) {
        const newW = maxX - adjusted.x
        if (newW < 1) {
          adjusted.x = maxX - adjusted.w
          if (adjusted.x < minX) {
            adjusted.x = minX
            adjusted.w = innerCols
          }
        } else {
          adjusted.w = newW
        }
      }

      if (adjusted.y < 0) adjusted.y = 0
      if (adjusted.w < 1) adjusted.w = 1
      if (adjusted.h < 1) adjusted.h = 1

      return adjusted
    })
  }

  const compactSlots = (currentSlots, excludeId = null) => {
    const filtered = excludeId ? currentSlots.filter(s => s.id !== excludeId) : currentSlots
    const sorted = [...filtered].sort((a, b) => {
      if (a.y !== b.y) return a.y - b.y
      return a.x - b.x
    })
    const compacted = []

    sorted.forEach(slot => {
      let newY = 0
      for (let y = 0; y <= slot.y; y++) {
        const testSlot = { ...slot, y }
        const collides = compacted.some(s => isColliding(testSlot, s))
        if (!collides) {
          newY = y
        }
      }
      compacted.push({ ...slot, y: newY })
    })

    return ensureSlotsWithinGrid(compacted)
  }

  const ensureNoOverlaps = (slotsToCheck, direction = null) => {
    const result = [...slotsToCheck]
    let hasOverlaps = true
    let iterations = 0

    while (hasOverlaps && iterations < 100) {
      hasOverlaps = false
      iterations++

      for (let i = 0; i < result.length; i++) {
        for (let j = i + 1; j < result.length; j++) {
          if (isColliding(result[i], result[j])) {
            hasOverlaps = true

            if (!direction || direction === 'bottom') {
              result[j] = {
                ...result[j],
                y: result[i].y + result[i].h
              }
            }
          }
        }
      }
    }

    return result
  }

  const detectInteractionArea = (mouseX, mouseY, slot) => {
    const cellWidth = getCellWidth()
    const slotPixel = gridToPixel(slot.x, slot.y)
    const slotWidth = slot.w * cellWidth + (slot.w - 1) * grid.gap
    const slotHeight = slot.h * grid.rowHeight + (slot.h - 1) * grid.gap

    const edgeThreshold = 10

    const onLeftEdge = mouseX >= slotPixel.x && mouseX <= slotPixel.x + edgeThreshold
    const onRightEdge = mouseX >= slotPixel.x + slotWidth - edgeThreshold && mouseX <= slotPixel.x + slotWidth
    const onTopEdge = mouseY >= slotPixel.y && mouseY <= slotPixel.y + edgeThreshold
    const onBottomEdge = mouseY >= slotPixel.y + slotHeight - edgeThreshold && mouseY <= slotPixel.y + slotHeight

    let direction = null
    if (onLeftEdge && !onTopEdge && !onBottomEdge) direction = 'left'
    else if (onRightEdge && !onTopEdge && !onBottomEdge) direction = 'right'
    else if (onTopEdge && !onLeftEdge && !onRightEdge) direction = 'top'
    else if (onBottomEdge && !onLeftEdge && !onRightEdge) direction = 'bottom'
    else if (onTopEdge && onLeftEdge) direction = 'top-left'
    else if (onTopEdge && onRightEdge) direction = 'top-right'
    else if (onBottomEdge && onLeftEdge) direction = 'bottom-left'
    else if (onBottomEdge && onRightEdge) direction = 'bottom-right'

    if (direction) {
      return { type: 'resize', direction }
    }

    return { type: 'move', direction: null }
  }

  // Generate rails from current slot positions
  const generateRails = (currentSlots) => {
    const verticalRailsMap = new Map()
    const horizontalRailsMap = new Map()

    // Set boundary rails for inner content area
    verticalRailsMap.set(safeAreaCols, [[0, Infinity]])
    verticalRailsMap.set(safeAreaCols + innerCols, [[0, Infinity]])
    horizontalRailsMap.set(0, [[safeAreaCols, safeAreaCols + innerCols]])

    currentSlots.forEach(slot => {
      const yRange = [slot.y, slot.y + slot.h]

      if (!verticalRailsMap.has(slot.x)) {
        verticalRailsMap.set(slot.x, [])
      }
      verticalRailsMap.get(slot.x).push(yRange)

      if (!verticalRailsMap.has(slot.x + slot.w)) {
        verticalRailsMap.set(slot.x + slot.w, [])
      }
      verticalRailsMap.get(slot.x + slot.w).push(yRange)

      const xRange = [slot.x, slot.x + slot.w]

      if (!horizontalRailsMap.has(slot.y)) {
        horizontalRailsMap.set(slot.y, [])
      }
      horizontalRailsMap.get(slot.y).push(xRange)

      if (!horizontalRailsMap.has(slot.y + slot.h)) {
        horizontalRailsMap.set(slot.y + slot.h, [])
      }
      horizontalRailsMap.get(slot.y + slot.h).push(xRange)
    })

    return {
      vertical: Array.from(verticalRailsMap.keys()).sort((a, b) => a - b),
      horizontal: Array.from(horizontalRailsMap.keys()).sort((a, b) => a - b),
      verticalRanges: verticalRailsMap,
      horizontalRanges: horizontalRailsMap
    }
  }

  // Update rails whenever slots change
  useEffect(() => {
    let slotsToProcess = slots

    if (dragging && placeholderPosition) {
      slotsToProcess = [
        ...slots.filter(s => s.id !== dragging),
        { id: 'placeholder', ...placeholderPosition }
      ]
    } else if (resizing) {
      slotsToProcess = slots
    }

    const newRails = generateRails(slotsToProcess)
    setRails(newRails)
  }, [slots, dragging, placeholderPosition, resizing])

  // Move a rail and update all affected slots
  const moveRail = (railType, oldPosition, newPosition) => {
    const hasSlotsAtPosition = slots.some(slot => {
      if (railType === 'vertical') {
        return slot.x === oldPosition || slot.x + slot.w === oldPosition
      } else {
        return slot.y === oldPosition || slot.y + slot.h === oldPosition
      }
    })

    if (!hasSlotsAtPosition) {
      if (railType === 'vertical' && (oldPosition === safeAreaCols || oldPosition === safeAreaCols + innerCols)) {
        return slots
      }
      if (railType === 'horizontal' && oldPosition === 0) {
        return slots
      }
    }

    let constrainedPosition = newPosition
    if (railType === 'vertical') {
      constrainedPosition = Math.max(safeAreaCols, Math.min(safeAreaCols + innerCols, constrainedPosition))
    } else {
      constrainedPosition = Math.max(0, constrainedPosition)
    }

    for (const slot of slots) {
      if (railType === 'vertical') {
        if (slot.x === oldPosition) {
          const newW = slot.w - (constrainedPosition - oldPosition)
          if (newW < 1) {
            constrainedPosition = oldPosition + slot.w - 1
          }
        } else if (slot.x + slot.w === oldPosition) {
          const newW = slot.w + (constrainedPosition - oldPosition)
          if (newW < 1) {
            constrainedPosition = oldPosition - slot.w + 1
          }
        }
      } else {
        if (slot.y === oldPosition) {
          const newH = slot.h - (constrainedPosition - oldPosition)
          if (newH < 1) {
            constrainedPosition = oldPosition + slot.h - 1
          }
        } else if (slot.y + slot.h === oldPosition) {
          const newH = slot.h + (constrainedPosition - oldPosition)
          if (newH < 1) {
            constrainedPosition = oldPosition - slot.h + 1
          }
        }
      }
    }

    const delta = constrainedPosition - oldPosition
    if (delta === 0) return slots

    const updatedSlots = slots.map(slot => {
      if (railType === 'vertical') {
        if (slot.x === oldPosition) {
          const newW = Math.max(1, slot.w - delta)
          const actualDelta = slot.w - newW
          return { ...slot, x: oldPosition + actualDelta, w: newW }
        } else if (slot.x + slot.w === oldPosition) {
          const newW = Math.max(1, slot.w + delta)
          return { ...slot, w: newW }
        }
      } else {
        if (slot.y === oldPosition) {
          const newH = Math.max(1, slot.h - delta)
          const actualDelta = slot.h - newH
          return { ...slot, y: oldPosition + actualDelta, h: newH }
        } else if (slot.y + slot.h === oldPosition) {
          const newH = Math.max(1, slot.h + delta)
          return { ...slot, h: newH }
        }
      }
      return slot
    })

    return updatedSlots
  }

  // Find all slots that would be pushed in a given direction
  const findAllSlotsInPushPath = (resizingSlot, direction, allSlots) => {
    const toPush = []
    const processed = new Set()
    const originalResized = resizeOriginal || resizingSlot

    const checkSlot = (referenceSlot, dir) => {
      if (processed.has(referenceSlot.id)) return
      processed.add(referenceSlot.id)

      allSlots.forEach(slot => {
        if (slot.id === resizingSlot.id || processed.has(slot.id)) return

        let isAdjacent = false

        if (dir === 'right') {
          const verticalOverlap = !(slot.y >= referenceSlot.y + referenceSlot.h || slot.y + slot.h <= referenceSlot.y)
          isAdjacent = slot.x === referenceSlot.x + referenceSlot.w && verticalOverlap
        } else if (dir === 'left') {
          const verticalOverlap = !(slot.y >= referenceSlot.y + referenceSlot.h || slot.y + slot.h <= referenceSlot.y)
          isAdjacent = slot.x + slot.w === referenceSlot.x && verticalOverlap
        } else if (dir === 'bottom') {
          const horizontalOverlap = !(slot.x >= referenceSlot.x + referenceSlot.w || slot.x + slot.w <= referenceSlot.x)
          isAdjacent = slot.y === referenceSlot.y + referenceSlot.h && horizontalOverlap
        } else if (dir === 'top') {
          const horizontalOverlap = !(slot.x >= referenceSlot.x + referenceSlot.w || slot.x + slot.w <= referenceSlot.x)
          isAdjacent = slot.y + slot.h === referenceSlot.y && horizontalOverlap
        }

        if (isAdjacent) {
          toPush.push(slot)
          checkSlot(slot, dir)
        }
      })
    }

    checkSlot(originalResized, direction)
    return toPush
  }

  // Calculate maximum push amount before hitting grid boundaries
  const calculateMaxPushFromOriginalPositions = (slotsToPush, direction, originalSlots) => {
    if (slotsToPush.length === 0) return Infinity

    let maxPush = Infinity
    const minX = safeAreaCols
    const maxX = safeAreaCols + innerCols

    for (const slot of slotsToPush) {
      const originalSlot = originalSlots.find(s => s.id === slot.id) || slot

      if (direction === 'right') {
        maxPush = Math.min(maxPush, maxX - (originalSlot.x + originalSlot.w))
      } else if (direction === 'left') {
        maxPush = Math.min(maxPush, originalSlot.x - minX)
      } else if (direction === 'bottom') {
        maxPush = Infinity
      } else if (direction === 'top') {
        maxPush = Math.min(maxPush, originalSlot.y)
      }
    }

    return Math.max(0, maxPush)
  }

  const calculateResizeFromMouse = (mouseX, mouseY, slot, direction) => {
    const cellWidth = getCellWidth()
    const gridX = mouseX / (cellWidth + grid.gap)
    const gridY = mouseY / (grid.rowHeight + grid.gap)
    const minX = safeAreaCols
    const maxX = safeAreaCols + innerCols

    let newX = slot.x
    let newY = slot.y
    let newW = slot.w
    let newH = slot.h

    if (!direction) return { x: newX, y: newY, w: newW, h: newH }

    // Handle resize with push behavior (simplified for now)
    if (direction.includes('right')) {
      const targetW = Math.max(1, Math.round(gridX - slot.x))
      const growth = targetW - slot.w

      if (growth > 0) {
        const slotsToPush = findAllSlotsInPushPath(slot, 'right', resizeOriginalSlots.length > 0 ? resizeOriginalSlots : slots)
        const maxPush = calculateMaxPushFromOriginalPositions(slotsToPush, 'right', resizeOriginalSlots.length > 0 ? resizeOriginalSlots : slots)
        const constrainedGrowth = Math.min(growth, maxPush)
        newW = Math.max(1, slot.w + constrainedGrowth)
      } else {
        newW = Math.max(1, targetW)
      }
    }

    if (direction.includes('left')) {
      const targetX = Math.max(minX, Math.round(gridX))
      const growth = slot.x - targetX

      if (growth > 0) {
        const slotsToPush = findAllSlotsInPushPath(slot, 'left', resizeOriginalSlots.length > 0 ? resizeOriginalSlots : slots)
        const maxPush = calculateMaxPushFromOriginalPositions(slotsToPush, 'left', resizeOriginalSlots.length > 0 ? resizeOriginalSlots : slots)
        const constrainedGrowth = Math.min(growth, maxPush)
        newX = Math.max(minX, slot.x - constrainedGrowth)
        newW = Math.max(1, slot.w + constrainedGrowth)
      } else {
        newX = targetX
        newW = Math.max(1, slot.w - (targetX - slot.x))
      }
    }

    if (direction.includes('bottom')) {
      const targetH = Math.max(1, Math.round(gridY - slot.y))
      const growth = targetH - slot.h

      if (growth > 0) {
        const slotsToPush = findAllSlotsInPushPath(slot, 'bottom', resizeOriginalSlots.length > 0 ? resizeOriginalSlots : slots)
        const maxPush = calculateMaxPushFromOriginalPositions(slotsToPush, 'bottom', resizeOriginalSlots.length > 0 ? resizeOriginalSlots : slots)
        const constrainedGrowth = Math.min(growth, maxPush)
        newH = Math.max(1, slot.h + constrainedGrowth)
      } else {
        newH = Math.max(1, targetH)
      }
    }

    if (direction.includes('top')) {
      const targetY = Math.max(0, Math.round(gridY))
      const growth = slot.y - targetY

      if (growth > 0) {
        const slotsToPush = findAllSlotsInPushPath(slot, 'top', resizeOriginalSlots.length > 0 ? resizeOriginalSlots : slots)
        const maxPush = calculateMaxPushFromOriginalPositions(slotsToPush, 'top', resizeOriginalSlots.length > 0 ? resizeOriginalSlots : slots)
        const constrainedGrowth = Math.min(growth, maxPush)
        newY = Math.max(0, slot.y - constrainedGrowth)
        newH = Math.max(1, slot.h + constrainedGrowth)
      } else {
        newY = targetY
        newH = Math.max(1, slot.h - (targetY - slot.y))
      }
    }

    return { x: newX, y: newY, w: newW, h: newH }
  }

  const handleRearrangeDuringDrag = (mouseX, mouseY, draggedSlot) => {
    const gridPos = pixelToGrid(mouseX - dragOffset.x, mouseY - dragOffset.y)
    const minX = safeAreaCols
    const maxX = safeAreaCols + innerCols

    const targetPosition = {
      x: Math.max(minX, Math.min(maxX - draggedSlot.w, gridPos.x)),
      y: Math.max(0, gridPos.y),
      w: draggedSlot.w,
      h: draggedSlot.h
    }

    // Determine drag direction based on movement since last position
    let direction = null

    if (lastDragPositionRef.current) {
      const deltaX = targetPosition.x - lastDragPositionRef.current.x
      const deltaY = targetPosition.y - lastDragPositionRef.current.y

      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        direction = deltaX > 0 ? 'right' : 'left'
      } else if (Math.abs(deltaY) > 0) {
        direction = deltaY > 0 ? 'down' : 'up'
      }
    }

    // Update last position for next move
    lastDragPositionRef.current = targetPosition

    // Check if we should push slots out of the way
    if (direction) {
      const otherSlots = slots.filter(s => s.id !== dragging)
      const overlappingSlots = otherSlots.filter(s => isColliding(targetPosition, s))

      overlappingSlots.forEach(underlyingSlot => {
        // Check if the edges are 1 cell apart in the movement direction
        let shouldPush = false
        let pushDistance = 0
        let pushDirection = direction

        if (direction === 'left') {
          // Check if dragged slot's left edge is 1 cell away from underlying slot's left edge
          const edgeDistance = underlyingSlot.x - targetPosition.x
          if (edgeDistance === 1 || (edgeDistance >= 0 && edgeDistance <= 1)) {
            shouldPush = true
            pushDistance = draggedSlot.w
          }
        } else if (direction === 'right') {
          // Check if dragged slot's right edge is 1 cell away from underlying slot's right edge
          const draggedRightEdge = targetPosition.x + targetPosition.w
          const underlyingRightEdge = underlyingSlot.x + underlyingSlot.w
          const edgeDistance = draggedRightEdge - underlyingRightEdge
          if (edgeDistance === 1 || (edgeDistance >= 0 && edgeDistance <= 1)) {
            shouldPush = true
            pushDistance = draggedSlot.w
          }
        } else if (direction === 'up') {
          // Check if dragged slot's top edge is 1 cell away from underlying slot's top edge
          const edgeDistance = underlyingSlot.y - targetPosition.y
          if (edgeDistance === 1 || (edgeDistance >= 0 && edgeDistance <= 1)) {
            shouldPush = true
            pushDistance = draggedSlot.h
          }
        } else if (direction === 'down') {
          // Check if dragged slot's bottom edge is 1 cell away from underlying slot's bottom edge
          const draggedBottomEdge = targetPosition.y + targetPosition.h
          const underlyingBottomEdge = underlyingSlot.y + underlyingSlot.h
          const edgeDistance = draggedBottomEdge - underlyingBottomEdge
          if (edgeDistance === 1 || (edgeDistance >= 0 && edgeDistance <= 1)) {
            shouldPush = true
            pushDistance = draggedSlot.h
          }
        }

        if (shouldPush) {
          // Calculate new position for underlying slot (move in opposite direction)
          let newX = underlyingSlot.x
          let newY = underlyingSlot.y

          if (direction === 'left') {
            newX = underlyingSlot.x + pushDistance // Push right
          } else if (direction === 'right') {
            newX = underlyingSlot.x - pushDistance // Push left
          } else if (direction === 'up') {
            newY = underlyingSlot.y + pushDistance // Push down
          } else if (direction === 'down') {
            newY = underlyingSlot.y - pushDistance // Push up
          }

          // Check if new position is valid (within bounds and no collisions)
          const newPosition = { ...underlyingSlot, x: newX, y: newY }
          const withinBounds = newX >= minX && newX + underlyingSlot.w <= maxX && newY >= 0
          const noCollisions = !otherSlots.filter(s => s.id !== underlyingSlot.id).some(s => isColliding(newPosition, s))

          if (withinBounds && noCollisions) {
            // Update the underlying slot's position
            setSlots(currentSlots =>
              currentSlots.map(s =>
                s.id === underlyingSlot.id ? newPosition : s
              )
            )
          }
        }
      })
    }

    setPlaceholderPosition(targetPosition)
  }

  // Mouse event handlers
  const handleSlotMouseDown = (e, slot) => {
    e.preventDefault()
    const rect = containerRef.current.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top
    const slotPixel = gridToPixel(slot.x, slot.y)

    setCurrentMousePos({ x: mouseX, y: mouseY })

    const interaction = detectInteractionArea(mouseX, mouseY, slot)

    if (interaction.type === 'resize') {
      setSelectedSlot(slot)
      setShouldDeselectOnUp(false)
      setResizing(slot.id)
      setResizeDirection(interaction.direction)
      setResizeOriginal({ ...slot })
      setResizeOriginalSlots(slots.map(s => ({ ...s })))
      setDragOffset({
        x: mouseX - slotPixel.x,
        y: mouseY - slotPixel.y
      })
      isOperatingRef.current = true
    } else if (interaction.type === 'move') {
      const isAlreadySelected = selectedSlot && selectedSlot.id === slot.id

      if (!isAlreadySelected) {
        setSelectedSlot(slot)
        setShouldDeselectOnUp(false)
      } else {
        setShouldDeselectOnUp(true)
      }

      setDragging(slot.id)
      setDraggedSlotOriginal({ ...slot })
      setPlaceholderPosition(null)
      lastDragPositionRef.current = null // Reset drag position tracker

      setDragOffset({
        x: mouseX - slotPixel.x,
        y: mouseY - slotPixel.y
      })
      isOperatingRef.current = true
    }
  }

  const handleRailMouseDown = (e, railType, railPosition, trackerSlot, trackerEdge) => {
    e.preventDefault()
    e.stopPropagation()
    const rect = containerRef.current.getBoundingClientRect()

    if (railType === 'vertical') {
      const mouseX = e.clientX - rect.left
      const pixelX = gridToPixel(railPosition, 0).x
      setDragOffset({ x: mouseX - pixelX, y: 0 })
    } else {
      const mouseY = e.clientY - rect.top
      const pixelY = gridToPixel(0, railPosition).y
      setDragOffset({ x: 0, y: mouseY - pixelY })
    }

    setDraggingRail({
      type: railType,
      position: railPosition,
      trackerSlotId: trackerSlot.id,
      trackerEdge: trackerEdge
    })
    isOperatingRef.current = true
  }

  const handleSlotClick = (e, slot) => {
    e.stopPropagation()

    if (swapMode) {
      if (swapMode.sourceSlotId === slot.id) {
        setSwapMode(null)
        setSwapHoveredSlot(null)
      } else {
        swapSlots(swapMode.sourceSlotId, slot.id)
      }
    } else if (selectedSlot && selectedSlot.id === slot.id && !dragging && !resizing && !justFinishedOperationRef.current) {
      setSelectedSlot(null)
    }

    justFinishedOperationRef.current = false
  }

  const handleContainerClick = (e) => {
    if (e.target === e.currentTarget && !justFinishedOperationRef.current) {
      setSelectedSlot(null)
    }
    justFinishedOperationRef.current = false
  }

  const handleMouseMove = (e) => {
    if (!dragging && !draggingRail && !resizing) return

    const rect = containerRef.current.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top
    setCurrentMousePos({ x: mouseX, y: mouseY })

    if (shouldDeselectOnUp) {
      setShouldDeselectOnUp(false)
    }

    if (resizing) {
      const resizingSlot = slots.find(s => s.id === resizing)
      const originalSlot = resizeOriginal || resizingSlot

      if (resizingSlot && resizeDirection && originalSlot && resizeOriginalSlots.length > 0) {
        const newDimensions = calculateResizeFromMouse(mouseX, mouseY, originalSlot, resizeDirection)

        let deltaX = 0
        let deltaY = 0
        let pushDirX = null
        let pushDirY = null

        if (resizeDirection.includes('right')) {
          deltaX = (originalSlot.x + newDimensions.w) - (originalSlot.x + originalSlot.w)
          pushDirX = deltaX !== 0 ? 'right' : null
        } else if (resizeDirection.includes('left')) {
          deltaX = originalSlot.x - newDimensions.x
          pushDirX = deltaX !== 0 ? 'left' : null
        }

        if (resizeDirection.includes('bottom')) {
          deltaY = (originalSlot.y + newDimensions.h) - (originalSlot.y + originalSlot.h)
          pushDirY = deltaY !== 0 ? 'bottom' : null
        } else if (resizeDirection.includes('top')) {
          deltaY = originalSlot.y - newDimensions.y
          pushDirY = deltaY !== 0 ? 'top' : null
        }

        // Find slots to push in horizontal direction
        let slotsToMoveX = []
        if (pushDirX && Math.abs(deltaX) > 0) {
          slotsToMoveX = findAllSlotsInPushPath(
            originalSlot,
            pushDirX,
            resizeOriginalSlots
          )
        }

        // Find slots to push in vertical direction
        let slotsToMoveY = []
        if (pushDirY && Math.abs(deltaY) > 0) {
          slotsToMoveY = findAllSlotsInPushPath(
            originalSlot,
            pushDirY,
            resizeOriginalSlots
          )
        }

        const updatedSlots = resizeOriginalSlots.map(originalSlotPos => {
          if (originalSlotPos.id === resizing) {
            return { ...originalSlotPos, ...newDimensions }
          }

          let newSlot = { ...originalSlotPos }

          // Apply horizontal push
          const shouldMoveX = slotsToMoveX.find(sp => sp.id === originalSlotPos.id)
          if (shouldMoveX) {
            if (pushDirX === 'right') {
              newSlot.x = originalSlotPos.x + deltaX
            } else if (pushDirX === 'left') {
              newSlot.x = originalSlotPos.x - deltaX
            }
          }

          // Apply vertical push
          const shouldMoveY = slotsToMoveY.find(sp => sp.id === originalSlotPos.id)
          if (shouldMoveY) {
            if (pushDirY === 'bottom') {
              newSlot.y = originalSlotPos.y + deltaY
            } else if (pushDirY === 'top') {
              newSlot.y = originalSlotPos.y - deltaY
            }
          }

          return newSlot
        })
        setSlots(updatedSlots)
      }
    }

    if (draggingRail) {
      const gridPos = pixelToGrid(mouseX, mouseY)
      const newPosition = draggingRail.type === 'vertical' ? gridPos.x : gridPos.y

      if (newPosition !== draggingRail.position) {
        const updatedSlots = moveRail(draggingRail.type, draggingRail.position, newPosition)
        setSlots(updatedSlots)

        const trackerSlot = updatedSlots.find(s => s.id === draggingRail.trackerSlotId)
        if (trackerSlot) {
          let actualNewPosition
          if (draggingRail.trackerEdge === 'left') {
            actualNewPosition = trackerSlot.x
          } else if (draggingRail.trackerEdge === 'right') {
            actualNewPosition = trackerSlot.x + trackerSlot.w
          } else if (draggingRail.trackerEdge === 'top') {
            actualNewPosition = trackerSlot.y
          } else if (draggingRail.trackerEdge === 'bottom') {
            actualNewPosition = trackerSlot.y + trackerSlot.h
          }

          setDraggingRail({ ...draggingRail, position: actualNewPosition })
        }
      }
    }

    if (dragging) {
      const draggedSlot = slots.find(s => s.id === dragging)
      if (!draggedSlot) return

      // Calculate target position first to get the current drag destination
      const gridPos = pixelToGrid(mouseX - dragOffset.x, mouseY - dragOffset.y)
      const minX = safeAreaCols
      const maxX = safeAreaCols + innerCols
      const targetPosition = {
        x: Math.max(minX, Math.min(maxX - draggedSlot.w, gridPos.x)),
        y: Math.max(0, gridPos.y),
        w: draggedSlot.w,
        h: draggedSlot.h
      }

      // Update placeholder position during drag (no collision/rearrangement)
      handleRearrangeDuringDrag(mouseX, mouseY, draggedSlot)
    }
  }

  const handleMouseUp = (e) => {
    if (shouldDeselectOnUp) {
      setSelectedSlot(null)
      setShouldDeselectOnUp(false)
    }

    if (dragging) {
      const rect = containerRef.current.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top

      const draggedSlot = slots.find(s => s.id === dragging)
      if (draggedSlot && draggedSlotOriginal) {
        // Calculate the drop position from current mouse position
        const pixelX = mouseX - dragOffset.x
        const pixelY = mouseY - dragOffset.y
        const gridPos = pixelToGrid(pixelX, pixelY)
        const minX = safeAreaCols
        const maxX = safeAreaCols + innerCols
        const targetX = Math.max(minX, Math.min(maxX - draggedSlot.w, gridPos.x))
        const targetY = Math.max(0, gridPos.y)

        // Get all slots except the dragged one
        let workingSlots = slots.filter(s => s.id !== dragging)

        const testDroppedSlot = { ...draggedSlot, x: targetX, y: targetY }

        // Check if the position fits
        const droppedSlotFits = !workingSlots.some(s => isColliding(testDroppedSlot, s))

        if (droppedSlotFits) {
          // Place at target position
          workingSlots.push(testDroppedSlot)
        } else {
          // Find nearest available position
          let placed = false
          const minX = safeAreaCols
          const maxX = safeAreaCols + innerCols
          for (let tryY = 0; tryY < 20 && !placed; tryY++) {
            for (let tryX = minX; tryX <= maxX - draggedSlot.w && !placed; tryX++) {
              const testSlot = { ...draggedSlot, x: tryX, y: tryY }
              const fits = !workingSlots.some(s => isColliding(testSlot, s))
              if (fits) {
                workingSlots.push(testSlot)
                placed = true
              }
            }
          }
          if (!placed) {
            // Revert to original position
            workingSlots.push(draggedSlotOriginal)
          }
        }

        // Sort slots by position (top-to-bottom, left-to-right)
        const sortedSlots = workingSlots.sort((a, b) => {
          if (a.y === b.y) return a.x - b.x
          return a.y - b.y
        })

        // Determine drag direction
        const finalSlot = workingSlots.find(s => s.id === dragging)
        if (finalSlot) {
          const deltaX = finalSlot.x - draggedSlotOriginal.x
          const deltaY = finalSlot.y - draggedSlotOriginal.y
          let direction = ''

          if (deltaX > 0) direction = 'right'
          else if (deltaX < 0) direction = 'left'
          else if (deltaY > 0) direction = 'down'
          else if (deltaY < 0) direction = 'up'

        }

        // Reset isOperatingRef BEFORE setSlots so onChange can fire
        isOperatingRef.current = false
        setSlots(sortedSlots)

        // Set selected slot AFTER slots are updated and isOperating is false
        if (!shouldDeselectOnUp) {
          const updatedSlot = sortedSlots.find(s => s.id === dragging)
          if (updatedSlot) {
            // Temporarily set isOperating to true to prevent sync effect
            isOperatingRef.current = true
            setSelectedSlot(updatedSlot)
            // Will be cleared to false at end of mouseup
          }
        }

        if (onSlotMove) {
          const finalSlot = workingSlots.find(s => s.id === dragging)
          if (finalSlot && (finalSlot.x !== draggedSlotOriginal.x || finalSlot.y !== draggedSlotOriginal.y)) {
            // Pass all slots so parent can save all affected positions
            onSlotMove(dragging, { x: draggedSlotOriginal.x, y: draggedSlotOriginal.y }, { x: finalSlot.x, y: finalSlot.y }, sortedSlots)
          }
        }
      }
    }

    if (resizing) {
      if (selectedSlot && resizing === selectedSlot.id) {
        const updatedSlot = slots.find(s => s.id === resizing)
        if (updatedSlot) {
          setSelectedSlot(updatedSlot)
        }
      }

      if (onSlotResize && resizeOriginal) {
        const finalSlot = slots.find(s => s.id === resizing)
        if (finalSlot && (finalSlot.w !== resizeOriginal.w || finalSlot.h !== resizeOriginal.h || finalSlot.x !== resizeOriginal.x || finalSlot.y !== resizeOriginal.y)) {
          // Pass all slots so parent can save all affected positions
          onSlotResize(resizing, resizeOriginal, { x: finalSlot.x, y: finalSlot.y, w: finalSlot.w, h: finalSlot.h }, slots)
        }
      }
    }

    if (draggingRail && onRailMove) {
      onRailMove(draggingRail.type, draggingRail.position)
    }

    // Mark that we just finished an operation to prevent click from deselecting
    if (dragging || resizing || draggingRail) {
      justFinishedOperationRef.current = true
    }

    setDragging(null)
    setDraggingRail(null)
    setResizing(null)
    setResizeDirection(null)
    setResizeOriginal(null)
    setResizeOriginalSlots([])
    setDraggedSlotOriginal(null)
    setPlaceholderPosition(null)
    setShouldDeselectOnUp(false)

    // Delay clearing isOperatingRef to prevent sync effect from running too early
    setTimeout(() => {
      isOperatingRef.current = false
    }, 0)
  }

  useEffect(() => {
    if (dragging || draggingRail || resizing) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [dragging, draggingRail, resizing, resizeDirection, dragOffset, draggedSlotOriginal, slots])

  // Utility functions for slots
  const addSlot = () => {
    const w = 3
    const h = 2
    const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4']

    let createdSlot = null

    setSlots(currentSlots => {
      // Find next available position using current slots
      const minX = safeAreaCols
      const maxX = safeAreaCols + innerCols
      let position = { x: minX, y: 0 }
      for (let y = 0; y < 100; y++) {
        for (let x = minX; x <= maxX - w; x++) {
          const testSlot = { x, y, w, h }
          const collides = currentSlots.some(s => isColliding(testSlot, s))
          if (!collides) {
            position = { x, y }
            y = 100 // break outer loop
            break
          }
        }
        if (position.x !== minX || position.y !== 0) break
      }

      const validatedX = Math.max(minX, Math.min(maxX - w, position.x))
      const validatedY = Math.max(0, position.y)

      const newSlot = {
        id: nextId.current++,
        x: validatedX,
        y: validatedY,
        w,
        h,
        color: colors[Math.floor(Math.random() * colors.length)],
        label: String.fromCharCode(65 + (nextId.current - 2) % 26)
      }

      createdSlot = newSlot
      const updatedSlots = ensureNoOverlaps([...currentSlots, newSlot])
      return updatedSlots
    })

    if (createdSlot) {
      if (onSlotCreate) onSlotCreate(createdSlot)
      PubSub.publish(`grid.${id}.slot.created`, { editorId: id, slot: createdSlot })
    }

    return createdSlot
  }

  const removeSlot = (slotId) => {
    setSlots(currentSlots => {
      const updatedSlots = currentSlots.filter(s => s.id !== slotId)
      return compactSlots(updatedSlots)
    })

    // Clear selection if deleted slot was selected
    if (selectedSlot && selectedSlot.id === slotId) {
      setSelectedSlot(null)
    }

    if (onSlotDelete) onSlotDelete(slotId)
    PubSub.publish(`grid.${id}.slot.deleted`, { editorId: id, slotId })
  }

  const enterSwapMode = (slotId) => {
    setSwapMode({ sourceSlotId: slotId })
  }

  const exitSwapMode = () => {
    setSwapMode(null)
    setSwapHoveredSlot(null)
  }

  const swapSlots = (slot1Id, slot2Id) => {
    setSlots(currentSlots => {
      const slot1 = currentSlots.find(s => s.id === slot1Id)
      const slot2 = currentSlots.find(s => s.id === slot2Id)

      if (!slot1 || !slot2) return currentSlots

      return currentSlots.map(slot => {
        if (slot.id === slot1Id) {
          return { ...slot, x: slot2.x, y: slot2.y, w: slot2.w, h: slot2.h }
        } else if (slot.id === slot2Id) {
          return { ...slot, x: slot1.x, y: slot1.y, w: slot1.w, h: slot1.h }
        }
        return slot
      })
    })

    exitSwapMode()

    if (onSwapExecute) onSwapExecute(slot1Id, slot2Id)
  }

  // PubSub subscriptions
  useEffect(() => {
    const tokens = [
      PubSub.subscribe(`grid.${id}.slot.create`, (_msg, data) => {
        addSlot()
      }),
      PubSub.subscribe(`grid.${id}.slot.delete`, (_msg, data) => {
        if (data.slotId) {
          removeSlot(data.slotId)
        }
      }),
      PubSub.subscribe(`grid.${id}.clear`, () => {
        setSlots([])
        if (onClear) onClear()
      }),
    ]

    // Emit ready event
    PubSub.publish(`grid.${id}.ready`, { editorId: id })

    return () => {
      tokens.forEach(token => PubSub.unsubscribe(token))
    }
  }, [id])

  // Track previous slots to detect actual changes
  const prevSlotsForChangeRef = useRef(null)

  // Emit change event when slots change (only from user operations, not syncing)
  useEffect(() => {
    if (onChange && !isOperatingRef.current && !isSyncingRef.current) {
      // Convert slots back to content coordinates (remove safe area offset)
      const contentSlots = slots.map(slot => ({
        ...slot,
        x: slot.x - safeAreaCols
      }))

      // Create a key to detect if slots actually changed
      const currentKey = JSON.stringify(contentSlots.map(s => ({ id: s.id, x: s.x, y: s.y, w: s.w, h: s.h })))

      if (currentKey !== prevSlotsForChangeRef.current) {
        prevSlotsForChangeRef.current = currentKey
        onChange(contentSlots)
      }
    }
  }, [slots, onChange, safeAreaCols])

  // Calculate render values
  // Single source of truth: always use `slots` directly (positions updated in-place)
  const slotsToRender = slots

  const getMaxRow = () => {
    const maxFromSlots = Math.max(0, ...slotsToRender.map(s => s.y + s.h))
    const maxFromPlaceholder = placeholderPosition ? placeholderPosition.y + placeholderPosition.h : 0
    return Math.max(10, maxFromSlots, maxFromPlaceholder)
  }

  const cellWidth = getCellWidth()
  const containerHeight = (getMaxRow() + 1) * (grid.rowHeight + grid.gap) + grid.gap

  const draggedSlot = dragging ? slots.find(s => s.id === dragging) : null

  // Calculate boundary positions for red lines
  const leftBoundaryX = gridToPixel(safeAreaCols, 0).x
  const rightBoundaryX = gridToPixel(safeAreaCols + innerCols, 0).x

  return (
    <div
      ref={containerRef}
      className={`grid-editor ${className}`}
      data-grid-id={id}
      onClick={handleContainerClick}
      style={{
        position: 'relative',
        width: '100%',
        minHeight: '400px',
        height: containerHeight,
        backgroundColor: 'rgb(var(--color-paper))',
        borderRadius: '12px',
        border: '2px solid rgb(var(--color-bordered))',
        backgroundImage: `
          repeating-linear-gradient(0deg, transparent, transparent ${grid.rowHeight}px, rgb(var(--color-admin_bordered)) ${grid.rowHeight}px, rgb(var(--color-admin_bordered)) ${grid.rowHeight + grid.gap}px),
          repeating-linear-gradient(90deg, transparent, transparent ${cellWidth}px, rgb(var(--color-admin_bordered)) ${cellWidth}px, rgb(var(--color-admin_bordered)) ${cellWidth + grid.gap}px)
        `,
        backgroundSize: `${cellWidth + grid.gap}px ${grid.rowHeight + grid.gap}px`,
        backgroundPosition: `${grid.gap}px ${grid.gap}px`,
        transition: 'background-image 150ms ease',
      }}
    >
      {/* Left boundary line */}
      <div
        style={{
          position: 'absolute',
          left: leftBoundaryX,
          top: 0,
          width: 2,
          height: containerHeight,
          backgroundColor: 'rgb(var(--color-error))',
          zIndex: 5,
          pointerEvents: 'none',
        }}
      />

      {/* Right boundary line */}
      <div
        style={{
          position: 'absolute',
          left: rightBoundaryX,
          top: 0,
          width: 2,
          height: containerHeight,
          backgroundColor: 'rgb(var(--color-error))',
          zIndex: 5,
          pointerEvents: 'none',
        }}
      />
      {/* Ghost position */}
      {dragging && draggedSlotOriginal && (
        <div
          style={{
            position: 'absolute',
            left: gridToPixel(draggedSlotOriginal.x, draggedSlotOriginal.y).x,
            top: gridToPixel(draggedSlotOriginal.x, draggedSlotOriginal.y).y,
            width: draggedSlotOriginal.w * cellWidth + (draggedSlotOriginal.w - 1) * grid.gap,
            height: draggedSlotOriginal.h * grid.rowHeight + (draggedSlotOriginal.h - 1) * grid.gap,
            borderRadius: '8px',
            border: '2px dashed #ef4444',
            backgroundColor: '#fee2e2',
            opacity: 0.3,
            pointerEvents: 'none',
            zIndex: 50,
          }}
        />
      )}

      {/* Placeholder */}
      {dragging && placeholderPosition && (
        <div
          style={{
            position: 'absolute',
            left: gridToPixel(placeholderPosition.x, placeholderPosition.y).x,
            top: gridToPixel(placeholderPosition.x, placeholderPosition.y).y,
            width: placeholderPosition.w * cellWidth + (placeholderPosition.w - 1) * grid.gap,
            height: placeholderPosition.h * grid.rowHeight + (placeholderPosition.h - 1) * grid.gap,
            borderRadius: '8px',
            border: '2px dashed #3b82f6',
            backgroundColor: '#dbeafe',
            opacity: 0.5,
            pointerEvents: 'none',
            zIndex: 50,
            transition: 'all 150ms ease-out',
          }}
        />
      )}

      {/* Render slots */}
      {slotsToRender.map(slot => {
        const isDragging = dragging === slot.id
        // Use placeholder position for dragged slot so it appears at drop position when drag ends
        const displaySlot = isDragging && placeholderPosition ? placeholderPosition : slot
        const pixel = gridToPixel(displaySlot.x, displaySlot.y)
        const width = slot.w * cellWidth + (slot.w - 1) * grid.gap
        const height = slot.h * grid.rowHeight + (slot.h - 1) * grid.gap

        const isSelected = selectedSlot && selectedSlot.id === slot.id
        const isHovered = hoveredSlot === slot.id
        const isSwapSource = swapMode?.sourceSlotId === slot.id
        const isSwapTarget = swapMode?.sourceSlotId !== slot.id && swapHoveredSlot === slot.id

        return (
          <div
            key={slot.id}
            onClick={(e) => handleSlotClick(e, slot)}
            onMouseDown={(e) => {
              if (!swapMode) {
                handleSlotMouseDown(e, slot)
              }
            }}
            onMouseEnter={() => {
              if (swapMode && swapMode.sourceSlotId !== slot.id) {
                setSwapHoveredSlot(slot.id)
              }
            }}
            onMouseLeave={() => {
              if (swapMode) {
                setSwapHoveredSlot(null)
              }
            }}
            onMouseMove={(e) => {
              if (!readOnly && !swapMode) {
                const rect = containerRef.current.getBoundingClientRect()
                const mouseX = e.clientX - rect.left
                const mouseY = e.clientY - rect.top
                const interaction = detectInteractionArea(mouseX, mouseY, slot)

                const cursorMap = {
                  'right': 'e-resize',
                  'left': 'w-resize',
                  'bottom': 's-resize',
                  'top': 'n-resize',
                  'right-bottom': 'se-resize',
                  'right-top': 'ne-resize',
                  'left-bottom': 'sw-resize',
                  'left-top': 'nw-resize',
                  'bottom-right': 'se-resize',
                  'bottom-left': 'sw-resize',
                  'top-right': 'ne-resize',
                  'top-left': 'nw-resize',
                }

                e.currentTarget.style.cursor = interaction.type === 'resize'
                  ? (cursorMap[interaction.direction] || 'move')
                  : (readOnly ? 'default' : 'move')
              }
            }}
            style={{
              position: 'absolute',
              left: pixel.x,
              top: pixel.y,
              width,
              height,
              backgroundColor: slot.color || '#3b82f6',
              borderRadius: '8px',
              boxShadow: isSelected ? '0 0 0 3px rgba(59, 130, 246, 0.5)' : '0 2px 10px rgba(0,0,0,0.1)',
              border: isSwapSource ? '3px solid #fbbf24' : (isSwapTarget ? '3px dashed #10b981' : (isSelected ? '2px solid #3b82f6' : 'none')),
              cursor: swapMode ? (isSwapSource ? 'default' : 'pointer') : (readOnly ? 'default' : 'move'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '24px',
              fontWeight: 'bold',
              userSelect: 'none',
              transition: isDragging ? 'none' : 'all 150ms ease-out',
              opacity: isDragging ? 0.3 : (isSwapTarget ? 0.7 : 1),
              pointerEvents: isDragging ? 'none' : 'auto',
              transform: isSwapTarget ? 'scale(1.05)' : 'scale(1)',
              visibility: isDragging ? 'hidden' : 'visible',
            }}
          >
            {/* Render custom view or fallback to renderSlot/label */}
            {slot.view ? (
              <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
                {React.isValidElement(slot.view) ? slot.view : React.createElement(slot.view, { slot })}
              </div>
            ) : (
              renderSlot ? renderSlot(slot) : slot.label || slot.id
            )}

            {/* Action buttons */}
            {isSelected && !swapMode && !readOnly && (
              <div
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '4px',
                  zIndex: 10,
                }}
              >
                {/* Custom buttons from slot.buttons */}
                {slot.buttons && Array.isArray(slot.buttons) && slot.buttons.map((btn, idx) => (
                  <button
                    key={idx}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (btn.onClick) btn.onClick(slot)
                    }}
                    style={{
                      backgroundColor: btn.backgroundColor || 'rgba(107, 114, 128, 0.8)',
                      color: btn.color || 'white',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      fontWeight: '500',
                      transition: 'background-color 150ms',
                      cursor: 'pointer',
                      border: 'none',
                    }}
                    title={btn.title}
                  >
                    {btn.label}
                  </button>
                ))}

                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation()
                    enterSwapMode(slot.id)
                  }}
                  style={{
                    backgroundColor: 'rgba(59, 130, 246, 0.8)',
                    color: 'white',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    transition: 'background-color 150ms',
                    cursor: 'pointer',
                    border: 'none',
                  }}
                >
                  swap
                </button>
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation()
                    removeSlot(slot.id)
                  }}
                  style={{
                    backgroundColor: 'rgba(239, 68, 68, 0.8)',
                    color: 'white',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    transition: 'background-color 150ms',
                    cursor: 'pointer',
                    border: 'none',
                  }}
                >
                  delete
                </button>
              </div>
            )}
          </div>
        )
      })}

      {/* Dragged slot following cursor */}
      {draggedSlot && currentMousePos.x && (
        <div
          style={{
            position: 'absolute',
            left: currentMousePos.x - dragOffset.x,
            top: currentMousePos.y - dragOffset.y,
            width: draggedSlot.w * cellWidth + (draggedSlot.w - 1) * grid.gap,
            height: draggedSlot.h * grid.rowHeight + (draggedSlot.h - 1) * grid.gap,
            backgroundColor: draggedSlot.color,
            borderRadius: '8px',
            boxShadow: '0 15px 35px rgba(0,0,0,0.3)',
            cursor: 'move',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '24px',
            fontWeight: 'bold',
            userSelect: 'none',
            opacity: 0.85,
            zIndex: 1000,
            transform: 'scale(1.02) rotate(1deg)',
            transition: 'none',
            pointerEvents: 'none',
          }}
        >
          {/* Render custom view or fallback to renderSlot/label */}
          {draggedSlot.view ? (
            <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
              {React.isValidElement(draggedSlot.view) ? draggedSlot.view : React.createElement(draggedSlot.view, { slot: draggedSlot })}
            </div>
          ) : (
            renderSlot ? renderSlot(draggedSlot) : draggedSlot.label || draggedSlot.id
          )}
        </div>
      )}

      {/* Render rails - only for selected slot */}
      {rails.vertical.map(railX => {
        if (dragging && placeholderPosition) {
          const touchesPlaceholder = placeholderPosition.x === railX || placeholderPosition.x + placeholderPosition.w === railX
          if (!touchesPlaceholder) return null
        } else if (selectedSlot) {
          const currentSelectedSlot = slots.find(s => s.id === selectedSlot.id) || selectedSlot
          const touchesSelectedSlot = currentSelectedSlot.x === railX || currentSelectedSlot.x + currentSelectedSlot.w === railX
          if (!touchesSelectedSlot) return null
        } else {
          return null
        }

        const findTrackerSlot = () => {
          for (const slot of slots) {
            if (slot.x === railX) return { slot, edge: 'left' }
            if (slot.x + slot.w === railX) return { slot, edge: 'right' }
          }
          return null
        }

        const pixel = gridToPixel(railX, 0)
        const tracker = findTrackerSlot()

        return (
          <div
            key={`vrail-${railX}`}
            onMouseDown={(e) => {
              if (tracker) {
                handleRailMouseDown(e, 'vertical', railX, tracker.slot, tracker.edge)
              }
            }}
            style={{
              position: 'absolute',
              left: pixel.x - 1,
              top: 0,
              width: 2,
              height: containerHeight,
              backgroundColor: 'rgba(59, 130, 246, 0.4)',
              cursor: 'col-resize',
              zIndex: 100,
            }}
          />
        )
      })}

      {rails.horizontal.map(railY => {
        if (dragging && placeholderPosition) {
          const touchesPlaceholder = placeholderPosition.y === railY || placeholderPosition.y + placeholderPosition.h === railY
          if (!touchesPlaceholder) return null
        } else if (selectedSlot) {
          const currentSelectedSlot = slots.find(s => s.id === selectedSlot.id) || selectedSlot
          const touchesSelectedSlot = currentSelectedSlot.y === railY || currentSelectedSlot.y + currentSelectedSlot.h === railY
          if (!touchesSelectedSlot) return null
        } else {
          return null
        }

        const findTrackerSlot = () => {
          for (const slot of slots) {
            if (slot.y === railY) return { slot, edge: 'top' }
            if (slot.y + slot.h === railY) return { slot, edge: 'bottom' }
          }
          return null
        }

        const pixel = gridToPixel(0, railY)
        const tracker = findTrackerSlot()

        return (
          <div
            key={`hrail-${railY}`}
            onMouseDown={(e) => {
              if (tracker) {
                handleRailMouseDown(e, 'horizontal', railY, tracker.slot, tracker.edge)
              }
            }}
            style={{
              position: 'absolute',
              left: 0,
              top: pixel.y - 1,
              width: '100%',
              height: 2,
              backgroundColor: 'rgba(59, 130, 246, 0.4)',
              cursor: 'row-resize',
              zIndex: 100,
            }}
          />
        )
      })}
    </div>
  )
}